<template>
  <div class="simcontrol-dashboard">
    <!-- Header -->
    <div class="header-toolbar">
      <div class="header-content">
        <div class="logo-section">
          <div class="logo-icon"><span>📶</span></div>
          <h1 class="dashboard-title">SIM Control Dashboard</h1>
        </div>
        <div class="header-controls">
          <button class="header-refresh-btn" @click="loadData" :disabled="loading">
            <span class="refresh-icon">🔄</span><span>{{ loading ? 'Loading...' : 'Refresh' }}</span>
          </button>
          <div class="live-indicator">
            <div :class="['live-dot', { 'auto-refresh': loading }]"></div>
            <span style="color: white; font-size: 0.875rem;">{{ loading ? 'Loading...' : 'Live' }}</span>
          </div>
        </div>
      </div>
    </div>

    <div class="main-content">
      <!-- Export Report Popup -->
      <div v-if="exportPopupActive" class="simple-popup" @click.self="exportPopupActive = false">
        <div class="popup-content">
          <div class="popup-header">
            <h3>Export SIM Report</h3>
            <button class="close-btn" @click="exportPopupActive = false">×</button>
          </div>
          <div class="popup-body">
            <div class="search-group mb-3">
              <label class="search-label">Filename</label>
              <input type="text" class="search-input" v-model="exportFilename">
            </div>
            <div class="search-group mb-3">
              <label class="search-label">Format</label>
              <select class="search-input" v-model="exportFormat">
                <option value="xlsx">Excel (.xlsx)</option>
                <option value="csv">CSV (.csv)</option>
              </select>
            </div>
            <div class="search-group mb-3">
              <label class="search-label">Columns to Include</label>
              <div class="export-columns-grid">
                <label class="export-column-option" v-for="field in exportFields" :key="field.key">
                  <input type="checkbox" v-model="field.selected">
                  <span>{{ field.label }}</span>
                </label>
              </div>
            </div>
            <p style="color: #64748b; font-size: 0.875rem;">
              Exports the {{ filteredSims.length }} SIM(s) matching the current filters.
            </p>
          </div>
          <div style="padding: 0 20px 20px; display: flex; justify-content: flex-end;">
            <button class="apply-filters-btn" @click="exportReport">Export</button>
          </div>
        </div>
      </div>

      <!-- Statistics -->
      <div class="stats-section">
        <div class="stats-header"><h3 class="stats-title">Statistics Overview</h3></div>
        <div class="stats-wrapper">
          <div v-if="loading" class="loading-overlay"><div class="loader"></div></div>
          <div v-if="!loading" class="stats-container">
            <div class="stat-card total-stat">
              <div class="stat-label">Total SIMs</div>
              <div class="stat-value">{{ sims.length }}</div>
              <div class="stat-change positive">
                <span class="stat-icon">✓</span>
                <span>{{ statusCounts.MANAGED }} Managed · {{ statusCounts.SUSPENDED }} Suspended</span>
              </div>
            </div>
            <div class="stat-card" :class="criticalCount > 0 ? 'equipment-stat' : 'average-stat'">
              <div class="stat-label">Data Health</div>
              <div class="stat-value">{{ criticalCount + lowCount }}</div>
              <div class="stat-change negative">
                <span class="stat-icon">⚠</span>
                <span>{{ criticalCount }} Critical · {{ lowCount }} Low Balance</span>
              </div>
            </div>
            <div class="stat-card volume-stat">
              <div class="stat-label">Data Used This Month</div>
              <div class="stat-value">{{ formatMb(usageTotalMb) }}</div>
              <div class="stat-change positive">
                <span class="stat-icon">💧</span><span>All SIMs, this month</span>
              </div>
            </div>
            <div class="stat-card cost-stat">
              <div class="stat-label">This Month's SIM Cost</div>
              <div class="stat-value">{{ currentMonthCostLabel }}</div>
              <div class="stat-change positive">
                <span class="stat-icon">💵</span><span>Organisation-wide, this month</span>
              </div>
            </div>
            <div class="stat-card" :class="balanceIsNegative ? 'equipment-stat' : 'cost-stat'">
              <div class="stat-label">Organisation Balance</div>
              <div class="stat-value">{{ balanceLabel }}</div>
              <div class="stat-change" :class="balanceIsNegative ? 'negative' : 'positive'">
                <span class="stat-icon">💰</span><span>Available account balance</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Charts -->
      <div class="charts-section">
        <div class="chart-card">
          <div class="chart-header"><h3 class="chart-title">SIM Status</h3></div>
          <div class="chart-body">
            <div v-if="loading" class="loading-overlay"><div class="loader"></div></div>
            <vue-apex-charts v-if="!loading" type="donut" height="260" :options="statusChartOptions" :series="statusChartSeries" />
          </div>
        </div>
        <div class="chart-card">
          <div class="chart-header"><h3 class="chart-title">Monthly SIM Costs (Last 6 Months)</h3></div>
          <div class="chart-body">
            <div v-if="loading" class="loading-overlay"><div class="loader"></div></div>
            <vue-apex-charts v-if="!loading" type="bar" height="260" :options="costsChartOptions" :series="costsChartSeries" />
          </div>
        </div>
        <div class="chart-card">
          <div class="chart-header"><h3 class="chart-title">Monthly Data Usage (Last 6 Months)</h3></div>
          <div class="chart-body">
            <div v-if="loading" class="loading-overlay"><div class="loader"></div></div>
            <vue-apex-charts v-if="!loading" type="bar" height="260" :options="usageChartOptions" :series="usageChartSeries" />
          </div>
        </div>
        <div class="chart-card">
          <div class="chart-header"><h3 class="chart-title">Weekly Data Usage (Last 8 Weeks)</h3></div>
          <div class="chart-body">
            <div v-if="loading" class="loading-overlay"><div class="loader"></div></div>
            <vue-apex-charts v-if="!loading" type="bar" height="260" :options="weeklyUsageChartOptions" :series="weeklyUsageChartSeries" />
          </div>
        </div>
      </div>

      <!-- High Usage SIMs -->
      <div class="grid-container">
        <div class="grid-header">
          <div class="grid-title"><span class="report-name">Top 10 High Usage SIMs ({{ highUsagePeriodLabel }})</span></div>
          <div class="grid-controls">
            <label class="checkbox-option" style="padding: 0;">
              <input type="checkbox" v-model="highUsageNoRuleOnly">
              <span>No Rule Only</span>
            </label>
            <div class="rows-selector">
              <span class="rows-label">Period:</span>
              <select class="rows-select" v-model="highUsagePeriod">
                <option value="week">This Week</option>
                <option value="month">This Month</option>
              </select>
            </div>
          </div>
        </div>
        <p v-if="highUsageNoRuleOnly" style="padding: 8px 20px 0; color: #64748b; font-size: 0.8rem;">
          Checks the top {{ highUsageCandidatePoolSize }} SIMs by usage for a rule (the API only supports
          checking one SIM at a time) and shows the highest-usage ones with none. A rule-less SIM ranked
          below that pool won't appear here.
        </p>
        <div class="table-container">
          <div v-if="loading || (highUsageNoRuleOnly && checkingNoRuleCandidates)" class="loading-overlay"><div class="loader"></div></div>

          <table v-if="!loading && topUsageSims.length > 0" class="data-table">
            <thead>
              <tr>
                <th>Description</th>
                <th>MSISDN</th>
                <th>Network</th>
                <th>Status</th>
                <th>Data Balance</th>
                <th>Roaming</th>
                <th>Data Used ({{ highUsagePeriodLabel }})</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="sim in topUsageSims" :key="sim.id" @click="goToSimControl(sim)">
                <td>{{ sim.description }}</td>
                <td><code class="registration-badge">{{ sim.msisdn }}</code></td>
                <td>{{ sim.networkName }}</td>
                <td><span :class="['activity-badge', 'badge-' + sim.status]">{{ sim.status }}</span></td>
                <td><span :class="['status-pill', 'level-' + dataLevel(sim.data_balance_in_mb)]">{{ formatMb(sim.data_balance_in_mb) }}</span></td>
                <td><span :class="['status-dot-new', sim.roaming ? 'processed' : 'idle']"></span> {{ sim.roaming ? 'Yes' : 'No' }}</td>
                <td><span class="status-pill level-low">{{ formatMb(sim[highUsageKey]) }}</span></td>
              </tr>
            </tbody>
          </table>
          <div v-else-if="!loading" class="empty-state">
            <div class="empty-icon">💧</div>
            <div class="empty-title">No Usage Data</div>
            <div class="empty-description">No SIMs have recorded data usage yet this month</div>
          </div>
        </div>
      </div>

      <!-- Filters -->
      <div class="search-section">
        <div class="filter-header">
          <h3 class="filter-title">Filters</h3>
          <div class="filter-header-buttons">
            <button class="reset-filter-header-btn" @click="resetFilters">
              <span class="reset-icon">↻</span> Reset Filters
            </button>
          </div>
        </div>
        <div class="search-controls-wrapper">
          <div class="search-controls">
            <div class="search-group">
              <label class="search-label">Status</label>
              <select class="search-input" v-model="filterStatus" @change="loadData">
                <option v-for="opt in statusOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
              </select>
            </div>
            <div class="search-group">
              <label class="search-label">Network</label>
              <select class="search-input" v-model="filterNetworkId">
                <option :value="null">All Networks</option>
                <option v-for="n in networks" :key="n.id" :value="n.id">{{ n.name }}</option>
              </select>
            </div>
            <div class="search-group">
              <label class="search-label">Data Health</label>
              <select class="search-input" v-model="filterDataHealth">
                <option value="">All Data Levels</option>
                <option value="critical">Critical (≤10MB)</option>
                <option value="low">Low (≤50MB)</option>
                <option value="ok">OK (&gt;50MB)</option>
              </select>
            </div>
            <div class="search-group">
              <label class="search-label">Roaming</label>
              <select class="search-input" v-model="filterRoaming">
                <option value="">All SIMs</option>
                <option value="true">Roaming Enabled</option>
                <option value="false">Roaming Disabled</option>
              </select>
            </div>
            <div class="search-group">
              <label class="search-label">IMEI Lock</label>
              <select class="search-input" v-model="filterImeiLocked">
                <option value="">All SIMs</option>
                <option value="true">IMEI Locked</option>
                <option value="false">IMEI Unlocked</option>
              </select>
            </div>
            <div class="search-group">
              <label class="search-label">
                Visible Columns
                <span class="multi-badge">{{ visibleColumnKeys.length }}/{{ allColumns.length }}</span>
              </label>
              <div class="multi-select-container">
                <div class="multi-select-display" @click="toggleColumnsDropdown">
                  <span class="selected-items">{{ visibleColumnKeys.length }} of {{ allColumns.length }} columns</span>
                  <span class="dropdown-arrow">▼</span>
                </div>
                <div v-if="columnsDropdownOpen" class="multi-select-dropdown">
                  <div class="select-all-option">
                    <label class="checkbox-option">
                      <input type="checkbox" :checked="visibleColumnKeys.length === allColumns.length" @change="toggleAllColumns">
                      <span>Select All</span>
                    </label>
                  </div>
                  <div class="dropdown-options">
                    <label class="checkbox-option" v-for="col in allColumns" :key="col.key">
                      <input type="checkbox" :value="col.key" v-model="visibleColumnKeys">
                      <span>{{ col.label }}</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="filter-actions">
            <button class="apply-filters-btn" @click="exportPopupActive = true">📥 Export Report</button>
            <button class="reset-filters-btn" @click="loadData">Refresh Data</button>
          </div>
        </div>
      </div>

      <!-- Data Grid -->
      <div class="grid-container">
        <div class="grid-header">
          <div class="grid-title"><span class="report-name">SIM Details</span></div>
          <div class="grid-controls">
            <div class="rows-selector">
              <span class="rows-label">Show:</span>
              <select class="rows-select" v-model.number="itemsPerPage">
                <option :value="10">10 rows</option>
                <option :value="20">20 rows</option>
                <option :value="50">50 rows</option>
                <option :value="100">100 rows</option>
              </select>
            </div>
            <div class="grid-actions">
              <button class="action-button refresh-btn" @click="loadData" :disabled="loading">
                <span class="action-icon">🔄</span><span>{{ loading ? 'Loading...' : 'Refresh' }}</span>
              </button>
            </div>
          </div>
        </div>

        <div class="table-container">
          <div v-if="loading" class="loading-overlay"><div class="loader"></div></div>

          <table v-if="!loading && paginatedSims.length > 0" class="data-table">
            <thead>
              <tr>
                <th v-for="col in visibleColumns" :key="col.key" @click="setSort(col.key)">
                  {{ col.label }}<span v-if="sortKey === col.key">{{ sortAsc ? ' ▲' : ' ▼' }}</span>
                </th>
              </tr>
              <tr class="search-row">
                <th v-for="col in visibleColumns" :key="'s-' + col.key" class="search-cell">
                  <input type="text" class="column-search-input" placeholder="Search..."
                    v-model="columnSearch[col.key]">
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="sim in paginatedSims" :key="sim.id" @click="goToSimControl(sim)">
                <td v-if="isVisible('description')">{{ sim.description }}</td>
                <td v-if="isVisible('msisdn')"><code class="registration-badge">{{ sim.msisdn }}</code></td>
                <td v-if="isVisible('networkName')">{{ sim.networkName }}</td>
                <td v-if="isVisible('status')"><span :class="['activity-badge', 'badge-' + sim.status]">{{ sim.status }}</span></td>
                <td v-if="isVisible('network_status')">{{ sim.network_status || '-' }}</td>
                <td v-if="isVisible('data_balance_in_mb')"><span :class="['status-pill', 'level-' + dataLevel(sim.data_balance_in_mb)]">{{ formatMb(sim.data_balance_in_mb) }}</span></td>
                <td v-if="isVisible('airtime_balance')">{{ sim.airtime_balance }}</td>
                <td v-if="isVisible('sms_balance')">{{ sim.sms_balance }}</td>
                <td v-if="isVisible('usageThisMonthMb')">{{ formatMb(sim.usageThisMonthMb) }}</td>
                <td v-if="isVisible('rechargeCount')">{{ sim.rechargeCount }}</td>
                <td v-if="isVisible('roaming')"><span :class="['status-dot-new', sim.roaming ? 'processed' : 'idle']"></span> {{ sim.roaming ? 'Yes' : 'No' }}</td>
                <td v-if="isVisible('imei_locked')"><span :class="['status-dot-new', sim.imei_locked ? 'processed' : 'idle']"></span> {{ sim.imei_locked ? 'Locked' : 'Unlocked' }}</td>
                <td v-if="isVisible('last_balance_check')">{{ sim.last_balance_check_formatted }}</td>
              </tr>
            </tbody>
          </table>

          <div v-if="!loading && paginatedSims.length === 0" class="empty-state">
            <div class="empty-icon">📂</div>
            <div class="empty-title">No SIMs Found</div>
            <div class="empty-description">Try adjusting your filters or refresh the data</div>
          </div>
        </div>

        <div class="pagination-container" v-if="!loading && filteredSims.length > 0">
          <div class="pagination-info">Showing {{ startIndex + 1 }}–{{ endIndex }} of {{ filteredSims.length }} SIMs</div>
          <div class="pagination-controls">
            <button class="page-btn" @click="currentPage = 1" :disabled="currentPage === 1">First</button>
            <button class="page-btn" @click="currentPage--" :disabled="currentPage === 1">Prev</button>
            <button v-for="p in visiblePageNumbers" :key="p" class="page-btn page-number"
              :class="{ active: p === currentPage }" @click="currentPage = p">{{ p }}</button>
            <button class="page-btn" @click="currentPage++" :disabled="currentPage === totalPages">Next</button>
            <button class="page-btn" @click="currentPage = totalPages" :disabled="currentPage === totalPages">Last</button>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts';

const DATA_BALANCE_THRESHOLDS_MB = {
  critical: 10,
  low: 50,
};

const COLUMNS = [
  { key: 'description', label: 'Description' },
  { key: 'msisdn', label: 'MSISDN' },
  { key: 'networkName', label: 'Network' },
  { key: 'status', label: 'Status' },
  { key: 'network_status', label: 'Network Status' },
  { key: 'data_balance_in_mb', label: 'Data Balance' },
  { key: 'airtime_balance', label: 'Airtime Balance' },
  { key: 'sms_balance', label: 'SMS Balance' },
  { key: 'usageThisMonthMb', label: 'Usage This Month' },
  { key: 'rechargeCount', label: 'Recharges This Month' },
  { key: 'roaming', label: 'Roaming' },
  { key: 'imei_locked', label: 'IMEI Lock' },
  { key: 'last_balance_check', label: 'Last Balance Check' },
];

export default {
  name: 'SimControlMainDashboard',
  components: {
    VueApexCharts,
  },
  data() {
    return {
      sims: [],
      networks: [],
      monthlyCosts: [],
      monthlyUsage: [],
      weeklyUsage: [],
      usageTotalMb: 0,
      usageByMsisdn: {},
      usageByMsisdnWeek: {},
      rechargeByMsisdn: {},
      orgBalance: null,
      highUsagePeriod: 'month',
      highUsageNoRuleOnly: false,
      highUsageCandidatePoolSize: 30,
      checkingNoRuleCandidates: false,
      ruleCache: {},
      ruleCacheLoading: {},
      loading: false,
      allColumns: COLUMNS,
      visibleColumnKeys: COLUMNS.map((col) => col.key),
      columnsDropdownOpen: false,
      columnSearch: COLUMNS.reduce((acc, col) => { acc[col.key] = ''; return acc; }, {}),
      statusOptions: [
        { label: 'All Statuses', value: 'ALL' },
        { label: 'Managed', value: 'MANAGED' },
        { label: 'Suspended', value: 'SUSPENDED' },
        { label: 'Paused', value: 'PAUSED' },
        { label: 'Staged', value: 'STAGED' },
      ],
      filterStatus: 'ALL',
      filterNetworkId: null,
      filterDataHealth: '',
      filterRoaming: '',
      filterImeiLocked: '',
      sortKey: 'msisdn',
      sortAsc: true,
      currentPage: 1,
      itemsPerPage: 20,
      exportPopupActive: false,
      exportFilename: 'sim-control-report',
      exportFormat: 'xlsx',
      exportFields: [
        { key: 'description', label: 'Description', selected: true },
        { key: 'msisdn', label: 'MSISDN', selected: true },
        { key: 'networkName', label: 'Network', selected: true },
        { key: 'status', label: 'Status', selected: true },
        { key: 'network_status', label: 'Network Status', selected: true },
        { key: 'data_balance_in_mb', label: 'Data Balance (MB)', selected: true },
        { key: 'airtime_balance', label: 'Airtime Balance', selected: true },
        { key: 'sms_balance', label: 'SMS Balance', selected: true },
        { key: 'usageThisMonthMb', label: 'Usage This Month (MB)', selected: true },
        { key: 'rechargeCount', label: 'Recharges This Month', selected: true },
        { key: 'roaming', label: 'Roaming', selected: true },
        { key: 'imei_locked', label: 'IMEI Locked', selected: true },
        { key: 'last_balance_check_formatted', label: 'Last Balance Check', selected: true },
      ],
    };
  },
  computed: {
    statusCounts() {
      const counts = { MANAGED: 0, SUSPENDED: 0, PAUSED: 0, STAGED: 0 };
      this.sims.forEach((sim) => {
        if (counts[sim.status] !== undefined) counts[sim.status]++;
      });
      return counts;
    },
    statusChartSeries() {
      const c = this.statusCounts;
      return [c.MANAGED, c.SUSPENDED, c.PAUSED, c.STAGED];
    },
    statusChartOptions() {
      return {
        labels: ['Managed', 'Suspended', 'Paused', 'Staged'],
        colors: ['#27AE60', '#E74C3C', '#F39C12', '#3498DB'],
        legend: { position: 'bottom' },
        dataLabels: { enabled: true },
      };
    },
    costsChartSeries() {
      return [{ name: 'Total Cost', data: this.monthlyCosts.map((m) => parseFloat(m.total_cost) || 0) }];
    },
    costsChartOptions() {
      return {
        chart: { toolbar: { show: false } },
        colors: ['#D2691E'],
        xaxis: { categories: this.monthlyCosts.map((m) => m.month) },
        dataLabels: { enabled: false },
        plotOptions: { bar: { borderRadius: 4, columnWidth: '45%' } },
        noData: { text: 'No cost data available' },
      };
    },
    usageChartSeries() {
      return [{ name: 'Data Used (MB)', data: this.monthlyUsage.map((m) => m.totalMb) }];
    },
    usageChartOptions() {
      return {
        chart: { toolbar: { show: false } },
        colors: ['#3498DB'],
        xaxis: { categories: this.monthlyUsage.map((m) => m.month) },
        dataLabels: { enabled: false },
        plotOptions: { bar: { borderRadius: 4, columnWidth: '45%' } },
        noData: { text: 'No usage data available' },
      };
    },
    weeklyUsageChartSeries() {
      return [{ name: 'Data Used (MB)', data: this.weeklyUsage.map((w) => w.totalMb) }];
    },
    weeklyUsageChartOptions() {
      return {
        chart: { toolbar: { show: false } },
        colors: ['#9C27B0'],
        xaxis: { categories: this.weeklyUsage.map((w) => w.week) },
        dataLabels: { enabled: false },
        plotOptions: { bar: { borderRadius: 4, columnWidth: '55%' } },
        noData: { text: 'No usage data available' },
      };
    },
    currentMonthCostLabel() {
      const latest = this.monthlyCosts[this.monthlyCosts.length - 1];
      return latest ? this.formatCurrency(latest.total_cost) : '—';
    },
    criticalCount() {
      return this.sims.filter((sim) => this.dataLevel(sim.data_balance_in_mb) === 'critical').length;
    },
    lowCount() {
      return this.sims.filter((sim) => this.dataLevel(sim.data_balance_in_mb) === 'low').length;
    },
    balanceLabel() {
      return this.orgBalance !== null ? this.formatCurrency(this.orgBalance) : '—';
    },
    balanceIsNegative() {
      return Number(this.orgBalance) < 0;
    },
    visibleColumns() {
      return this.allColumns.filter((col) => this.visibleColumnKeys.includes(col.key));
    },
    enrichedSims() {
      return this.sims.map((sim) => {
        const usageThisMonthMb = parseFloat((this.usageByMsisdn[sim.msisdn] || {}).data_usage) || 0;
        const usageThisWeekMb = parseFloat((this.usageByMsisdnWeek[sim.msisdn] || {}).data_usage) || 0;
        return {
          ...sim,
          networkName: this.networkName(sim.network_id),
          last_balance_check_formatted: this.formatDate(sim.last_balance_check),
          usageThisMonthMb,
          usageThisWeekMb,
          rechargeCount: (this.rechargeByMsisdn[sim.msisdn] || {}).total || 0,
        };
      });
    },
    highUsagePeriodLabel() {
      return this.highUsagePeriod === 'week' ? 'This Week' : 'This Month';
    },
    highUsageKey() {
      return this.highUsagePeriod === 'week' ? 'usageThisWeekMb' : 'usageThisMonthMb';
    },
    highUsageCandidates() {
      const key = this.highUsageKey;
      return this.enrichedSims
        .filter((sim) => sim[key] > 0)
        .sort((a, b) => b[key] - a[key])
        .slice(0, this.highUsageCandidatePoolSize);
    },
    topUsageSims() {
      if (this.highUsageNoRuleOnly) {
        return this.highUsageCandidates
          .filter((sim) => this.ruleCache[sim.id] === '')
          .slice(0, 10);
      }
      return this.highUsageCandidates.slice(0, 10);
    },
    filteredSims() {
      const dataHealth = this.filterDataHealth;
      const roaming = this.filterRoaming;
      const imeiLocked = this.filterImeiLocked;

      let list = this.enrichedSims;

      if (this.filterNetworkId) list = list.filter((sim) => sim.network_id === this.filterNetworkId);
      if (dataHealth) list = list.filter((sim) => this.dataLevel(sim.data_balance_in_mb) === dataHealth);
      if (roaming !== '') list = list.filter((sim) => Boolean(sim.roaming) === (roaming === 'true'));
      if (imeiLocked !== '') list = list.filter((sim) => Boolean(sim.imei_locked) === (imeiLocked === 'true'));

      list = list.filter((sim) => this.matchesColumnSearch(sim));

      return this.sortSims(list);
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.filteredSims.length / this.itemsPerPage));
    },
    startIndex() {
      return (this.currentPage - 1) * this.itemsPerPage;
    },
    endIndex() {
      return Math.min(this.startIndex + this.itemsPerPage, this.filteredSims.length);
    },
    paginatedSims() {
      return this.filteredSims.slice(this.startIndex, this.endIndex);
    },
    visiblePageNumbers() {
      const total = this.totalPages;
      const current = Math.min(this.currentPage, total);
      let start = Math.max(1, current - 2);
      const end = Math.min(total, start + 4);
      start = Math.max(1, end - 4);
      const pages = [];
      for (let i = start; i <= end; i++) pages.push(i);
      return pages;
    },
  },
  watch: {
    filterNetworkId() { this.currentPage = 1; },
    filterDataHealth() { this.currentPage = 1; },
    filterRoaming() { this.currentPage = 1; },
    filterImeiLocked() { this.currentPage = 1; },
    columnSearch: {
      deep: true,
      handler() { this.currentPage = 1; },
    },
    paginatedSims: {
      immediate: true,
      handler(sims) {
        sims.forEach((sim) => this.loadRuleForSim(sim));
      },
    },
    highUsageCandidates: {
      immediate: true,
      handler(candidates) {
        if (this.highUsageNoRuleOnly) this.checkNoRuleCandidates(candidates);
      },
    },
    highUsageNoRuleOnly(value) {
      if (value) this.checkNoRuleCandidates(this.highUsageCandidates);
    },
  },
  methods: {
    loadRuleForSim(sim) {
      if (this.ruleCache[sim.id] !== undefined || this.ruleCacheLoading[sim.id]) return Promise.resolve();
      this.$set(this.ruleCacheLoading, sim.id, true);
      return this.ajaxGetAsync(`SimControl/rules?sim_id=${sim.id}&page_size=5`).then((response) => {
        const rules = (response && response.data && response.data.data) || [];
        const label = rules.length ? rules.map((r) => r.name).join(', ') : '';
        this.$set(this.ruleCache, sim.id, label);
      });
    },
    async checkNoRuleCandidates(candidates) {
      const pending = candidates.filter((sim) => this.ruleCache[sim.id] === undefined && !this.ruleCacheLoading[sim.id]);
      if (pending.length === 0) return;
      this.checkingNoRuleCandidates = true;
      await Promise.all(pending.map((sim) => this.loadRuleForSim(sim)));
      this.checkingNoRuleCandidates = false;
    },
    dataLevel(mb) {
      const value = parseFloat(mb) || 0;
      if (value <= DATA_BALANCE_THRESHOLDS_MB.critical) return 'critical';
      if (value <= DATA_BALANCE_THRESHOLDS_MB.low) return 'low';
      return 'ok';
    },
    networkName(id) {
      const network = this.networks.find((n) => n.id === id);
      return network ? network.name : id != null ? `Network ${id}` : 'Unknown';
    },
    formatDate(value) {
      return value ? new Date(value).toLocaleString() : '-';
    },
    formatMb(mb) {
      const value = parseFloat(mb) || 0;
      return value >= 1024 ? `${(value / 1024).toFixed(1)} GB` : `${value.toFixed(1)} MB`;
    },
    formatCurrency(value) {
      return value != null && value !== '' ? `R ${Number(value).toFixed(2)}` : '—';
    },
    goToSimControl(sim) {
      window.open(`https://app.simcontrol.co.za/tecmo-automation/sims/${sim.id}`, '_blank');
    },
    setSort(key) {
      if (this.sortKey === key) this.sortAsc = !this.sortAsc;
      else { this.sortKey = key; this.sortAsc = true; }
    },
    sortSims(list) {
      const key = this.sortKey;
      const dir = this.sortAsc ? 1 : -1;
      const numericKeys = ['data_balance_in_mb', 'airtime_balance', 'sms_balance', 'usageThisMonthMb', 'rechargeCount'];
      return list.slice().sort((a, b) => {
        if (numericKeys.includes(key)) return ((parseFloat(a[key]) || 0) - (parseFloat(b[key]) || 0)) * dir;
        return String(a[key] || '').localeCompare(String(b[key] || '')) * dir;
      });
    },
    matchesColumnSearch(sim) {
      const displayValues = {
        description: sim.description,
        msisdn: sim.msisdn,
        networkName: sim.networkName,
        status: sim.status,
        network_status: sim.network_status,
        data_balance_in_mb: this.formatMb(sim.data_balance_in_mb),
        airtime_balance: sim.airtime_balance,
        sms_balance: sim.sms_balance,
        usageThisMonthMb: this.formatMb(sim.usageThisMonthMb),
        rechargeCount: sim.rechargeCount,
        roaming: sim.roaming ? 'Yes' : 'No',
        imei_locked: sim.imei_locked ? 'Locked' : 'Unlocked',
        last_balance_check: sim.last_balance_check_formatted,
      };
      return this.visibleColumns.every((col) => {
        const filterValue = (this.columnSearch[col.key] || '').trim().toLowerCase();
        if (!filterValue) return true;
        return String(displayValues[col.key] == null ? '' : displayValues[col.key]).toLowerCase().includes(filterValue);
      });
    },
    toggleColumnsDropdown() {
      this.columnsDropdownOpen = !this.columnsDropdownOpen;
    },
    toggleAllColumns(event) {
      this.visibleColumnKeys = event.target.checked ? this.allColumns.map((col) => col.key) : [];
    },
    isVisible(key) {
      return this.visibleColumnKeys.includes(key);
    },
    resetFilters() {
      this.allColumns.forEach((col) => { this.columnSearch[col.key] = ''; });
      this.filterNetworkId = null;
      this.filterDataHealth = '';
      this.filterRoaming = '';
      this.filterImeiLocked = '';
      this.filterStatus = 'ALL';
      this.loadData();
    },
    exportReport() {
      import('@/vendor/Export2Excel').then((excel) => {
        const selectedFields = this.exportFields.filter((field) => field.selected);
        const rows = this.filteredSims.map((sim) => selectedFields.map((field) => {
          const value = sim[field.key];
          if (field.key === 'roaming' || field.key === 'imei_locked') return value ? 'Yes' : 'No';
          return value;
        }));
        excel.export_json_to_excel({
          header: selectedFields.map((field) => field.label),
          data: rows,
          filename: this.exportFilename || 'sim-control-report',
          autoWidth: true,
          bookType: this.exportFormat,
        });
        this.exportPopupActive = false;
      });
    },
    ajaxGetAsync(url) {
      const self = this;
      return new Promise((resolve) => {
        let result = null;
        self.$ajaxGet(self, url, (response) => { result = response; }, () => resolve(result));
      });
    },
    async fetchAllSims(status) {
      // page_size is the SIMcontrol API's stated maximum - loop keeps paging
      // until a short page confirms every SIM (not just the first page) is in.
      let all = [];
      let page = 1;
      const pageSize = 1000;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const response = await this.ajaxGetAsync(`SimControl?page=${page}&page_size=${pageSize}&status=${status}`);
        const pageItems = response && Array.isArray(response.data) ? response.data : [];
        all = all.concat(pageItems);
        if (pageItems.length < pageSize) break;
        page++;
      }
      return all;
    },
    async fetchAllRecharges() {
      let all = [];
      let page = 1;
      const pageSize = 1000;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const response = await this.ajaxGetAsync(`SimControl/recharges?page=${page}&page_size=${pageSize}`);
        const pageItems = (response && response.data && response.data.data) || [];
        all = all.concat(pageItems);
        if (pageItems.length < pageSize) break;
        page++;
      }
      return all;
    },
    async loadRecharges() {
      const rows = await this.fetchAllRecharges();
      const map = {};
      rows.forEach((row) => {
        if (!row.msisdn) return;
        if (!map[row.msisdn]) map[row.msisdn] = { total: 0, success: 0 };
        map[row.msisdn].total++;
        if (row.status === 'SUCCESS') map[row.msisdn].success++;
      });
      this.rechargeByMsisdn = map;
    },
    async loadNetworks() {
      const response = await this.ajaxGetAsync('SimControl/networks');
      this.networks = (response && response.data && response.data.data) || [];
    },
    async loadUsage() {
      const response = await this.ajaxGetAsync('SimControl/usage');
      const data = (response && response.data && response.data.data) || {};
      this.usageByMsisdn = data;
      this.usageTotalMb = Object.values(data).reduce((sum, entry) => sum + (parseFloat(entry.data_usage) || 0), 0);
    },
    async loadCosts() {
      const response = await this.ajaxGetAsync('SimControl/costs');
      this.monthlyCosts = (response && response.data && response.data.data) || [];
    },
    async loadBalance() {
      const response = await this.ajaxGetAsync('SimControl/balance');
      this.orgBalance = response && response.data ? response.data.balance : null;
    },
    toDateString(d) {
      return d.toISOString().slice(0, 10);
    },
    mondayOf(date) {
      const daysSinceMonday = (date.getDay() + 6) % 7;
      return new Date(date.getFullYear(), date.getMonth(), date.getDate() - daysSinceMonday);
    },
    async loadUsageThisWeek() {
      const now = new Date();
      const monday = this.mondayOf(now);
      const response = await this.ajaxGetAsync(`SimControl/usage?start_date=${this.toDateString(monday)}&end_date=${this.toDateString(now)}`);
      this.usageByMsisdnWeek = (response && response.data && response.data.data) || {};
    },
    async loadWeeklyUsageTrend() {
      const now = new Date();
      const thisMonday = this.mondayOf(now);
      const weeks = [];
      for (let i = 7; i >= 0; i--) {
        const weekStart = new Date(thisMonday.getFullYear(), thisMonday.getMonth(), thisMonday.getDate() - i * 7);
        const weekEnd = new Date(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate() + 6);
        weeks.push({
          label: `${weekStart.getDate()}/${weekStart.getMonth() + 1}`,
          start: this.toDateString(weekStart),
          end: this.toDateString(weekEnd > now ? now : weekEnd),
        });
      }
      const responses = await Promise.all(weeks.map((w) => this.ajaxGetAsync(`SimControl/usage?start_date=${w.start}&end_date=${w.end}`)));
      this.weeklyUsage = weeks.map((w, i) => {
        const response = responses[i];
        const data = (response && response.data && response.data.data) || {};
        const totalMb = Object.values(data).reduce((sum, entry) => sum + (parseFloat(entry.data_usage) || 0), 0);
        return { week: w.label, totalMb: Math.round(totalMb * 10) / 10 };
      });
    },
    async loadMonthlyUsageTrend() {
      const now = new Date();
      const months = [];
      for (let i = 5; i >= 0; i--) {
        const monthStart = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const monthEnd = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 0);
        months.push({
          label: monthStart.toLocaleString('default', { month: 'short', year: 'numeric' }),
          start: this.toDateString(monthStart),
          end: this.toDateString(monthEnd > now ? now : monthEnd),
        });
      }
      const responses = await Promise.all(months.map((m) => this.ajaxGetAsync(`SimControl/usage?start_date=${m.start}&end_date=${m.end}`)));
      this.monthlyUsage = months.map((m, i) => {
        const response = responses[i];
        const data = (response && response.data && response.data.data) || {};
        const totalMb = Object.values(data).reduce((sum, entry) => sum + (parseFloat(entry.data_usage) || 0), 0);
        return { month: m.label, totalMb: Math.round(totalMb * 10) / 10 };
      });
    },
    async loadData() {
      this.loading = true;
      try {
        const [sims] = await Promise.all([
          this.fetchAllSims(this.filterStatus),
          this.loadNetworks(),
          this.loadUsage(),
          this.loadUsageThisWeek(),
          this.loadCosts(),
          this.loadRecharges(),
          this.loadBalance(),
          this.loadMonthlyUsageTrend(),
          this.loadWeeklyUsageTrend(),
        ]);
        this.sims = sims;
        this.currentPage = 1;
      } finally {
        this.loading = false;
      }
    },
  },
  mounted() {
    this.loadData();
  },
};
</script>

<style>
.simcontrol-dashboard {
  --autumn-primary: #D2691E;
  --autumn-accent: #FF8C00;
  --autumn-light: #FFE4B5;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.simcontrol-dashboard * {
  box-sizing: border-box;
}

.simcontrol-dashboard .header-toolbar {
  background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
  padding: 16px 20px;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  margin-bottom: 20px;
}

.simcontrol-dashboard .header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.simcontrol-dashboard .logo-section {
  display: flex;
  align-items: center;
  gap: 12px;
}

.simcontrol-dashboard .logo-icon {
  width: 40px;
  height: 40px;
  background: white;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
}

.simcontrol-dashboard .dashboard-title {
  color: white;
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
}

.simcontrol-dashboard .header-controls {
  display: flex;
  align-items: center;
  gap: 12px;
}

.simcontrol-dashboard .header-refresh-btn {
  padding: 8px 16px;
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 6px;
  color: white;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  gap: 6px;
}

.simcontrol-dashboard .header-refresh-btn:hover {
  background: rgba(255, 255, 255, 0.3);
}

.simcontrol-dashboard .live-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.2);
  padding: 8px 16px;
  border-radius: 20px;
}

.simcontrol-dashboard .live-dot {
  width: 8px;
  height: 8px;
  background: #4CAF50;
  border-radius: 50%;
  animation: sc-pulse 2s infinite;
}

.simcontrol-dashboard .live-dot.auto-refresh {
  background: #f39c12;
}

@keyframes sc-pulse {
  0% { box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.7); }
  70% { box-shadow: 0 0 0 10px rgba(76, 175, 80, 0); }
  100% { box-shadow: 0 0 0 0 rgba(76, 175, 80, 0); }
}

.simcontrol-dashboard .stats-section,
.simcontrol-dashboard .search-section,
.simcontrol-dashboard .grid-container,
.simcontrol-dashboard .chart-card {
  background: #fff;
  border-radius: 12px;
  margin-bottom: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
  border: 1px solid #cbd5e1;
}

.simcontrol-dashboard .grid-container,
.simcontrol-dashboard .chart-card {
  overflow: hidden;
}

.simcontrol-dashboard .stats-header,
.simcontrol-dashboard .filter-header,
.simcontrol-dashboard .grid-header,
.simcontrol-dashboard .chart-header {
  padding: 16px 20px;
  background: #f1f5f9;
  border-bottom: 1px solid #cbd5e1;
  border-radius: 12px 12px 0 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.simcontrol-dashboard .stats-title,
.simcontrol-dashboard .filter-title,
.simcontrol-dashboard .chart-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #34495E;
  margin: 0;
}

.simcontrol-dashboard .stats-wrapper,
.simcontrol-dashboard .search-controls-wrapper,
.simcontrol-dashboard .chart-body {
  padding: 16px 20px;
}

.simcontrol-dashboard .stats-wrapper,
.simcontrol-dashboard .chart-body {
  position: relative;
  min-height: 100px;
}

.simcontrol-dashboard .chart-body {
  min-height: 260px;
}

.simcontrol-dashboard .stats-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
}

.simcontrol-dashboard .charts-section {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 20px;
}

.simcontrol-dashboard .stat-card {
  background: #f8fafc;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  transition: all 0.3s;
  border: 1px solid #e2e8f0;
}

.simcontrol-dashboard .total-stat { border-left: 4px solid #FF8C00; }
.simcontrol-dashboard .volume-stat { border-left: 4px solid #4CAF50; }
.simcontrol-dashboard .cost-stat { border-left: 4px solid #27AE60; }
.simcontrol-dashboard .equipment-stat { border-left: 4px solid #E74C3C; }
.simcontrol-dashboard .average-stat { border-left: 4px solid #9C27B0; }

.simcontrol-dashboard .stat-label {
  color: #2c3e50;
  font-size: 0.875rem;
  margin-bottom: 8px;
  font-weight: 600;
}

.simcontrol-dashboard .stat-value {
  font-size: 1.75rem;
  color: #2c3e50;
  font-weight: bold;
}

.simcontrol-dashboard .stat-change {
  font-size: 0.75rem;
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.simcontrol-dashboard .stat-change.positive { color: #27AE60; }
.simcontrol-dashboard .stat-change.negative { color: #E74C3C; }

.simcontrol-dashboard .filter-header-buttons { display: flex; gap: 10px; }

.simcontrol-dashboard .reset-filter-header-btn {
  padding: 8px 16px;
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  border: none;
  color: white;
  font-size: 0.9rem;
  font-weight: 500;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  gap: 6px;
}

.simcontrol-dashboard .reset-filter-header-btn:hover {
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
}

.simcontrol-dashboard .search-controls {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}

.simcontrol-dashboard .search-group {
  display: flex;
  flex-direction: column;
}

.simcontrol-dashboard .search-label {
  font-size: 0.875rem;
  color: #34495E;
  margin-bottom: 4px;
  font-weight: 500;
}

.simcontrol-dashboard .search-input {
  padding: 10px 12px;
  border: 1px solid #94a3b8;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.3s;
  background: #fff;
}

.simcontrol-dashboard .search-input:focus {
  outline: none;
  border-color: #D2691E;
  box-shadow: 0 0 0 3px rgba(210, 105, 30, 0.15);
}

.simcontrol-dashboard .multi-badge {
  background: #FF8C00;
  color: white;
  padding: 1px 6px;
  border-radius: 10px;
  font-size: 0.7rem;
  font-weight: 600;
  margin-left: 4px;
}

.simcontrol-dashboard .multi-select-container {
  position: relative;
}

.simcontrol-dashboard .multi-select-display {
  padding: 10px 12px;
  border: 1px solid #94a3b8;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 44px;
}

.simcontrol-dashboard .multi-select-display:hover {
  border-color: #D2691E;
  background: #fff8f3;
}

.simcontrol-dashboard .selected-items {
  color: #495057;
  font-weight: 500;
  font-size: 0.875rem;
}

.simcontrol-dashboard .dropdown-arrow {
  color: #999;
  font-size: 0.75rem;
}

.simcontrol-dashboard .multi-select-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 2px solid #D2691E;
  border-radius: 8px;
  margin-top: 4px;
  max-height: 260px;
  overflow-y: auto;
  z-index: 9999;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
}

.simcontrol-dashboard .select-all-option {
  padding: 8px 12px;
  border-bottom: 1px solid #f0f0f0;
  background: #f8f9fa;
}

.simcontrol-dashboard .dropdown-options {
  max-height: 210px;
  overflow-y: auto;
}

.simcontrol-dashboard .checkbox-option {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  cursor: pointer;
  transition: background 0.2s;
  font-size: 0.875rem;
}

.simcontrol-dashboard .checkbox-option:hover {
  background: #fff3e0;
}

.simcontrol-dashboard .checkbox-option input[type="checkbox"] {
  margin-right: 8px;
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: #D2691E;
}

.simcontrol-dashboard .filter-actions {
  display: flex;
  gap: 12px;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
}

.simcontrol-dashboard .apply-filters-btn {
  padding: 10px 24px;
  background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
}

.simcontrol-dashboard .apply-filters-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(210, 105, 30, 0.3);
}

.simcontrol-dashboard .reset-filters-btn {
  padding: 10px 24px;
  background: white;
  color: #34495E;
  border: 2px solid #FFE4B5;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
}

.simcontrol-dashboard .reset-filters-btn:hover {
  background: #FFF8F3;
  border-color: #FF8C00;
}

.simcontrol-dashboard .grid-controls {
  display: flex;
  align-items: center;
  gap: 20px;
}

.simcontrol-dashboard .rows-selector {
  display: flex;
  align-items: center;
  gap: 8px;
}

.simcontrol-dashboard .rows-label {
  font-size: 0.875rem;
  color: #6c757d;
  font-weight: 500;
}

.simcontrol-dashboard .rows-select {
  padding: 6px 12px;
  border: 1px solid #94a3b8;
  border-radius: 6px;
  background: white;
  color: #475569;
  font-size: 0.875rem;
  cursor: pointer;
}

.simcontrol-dashboard .action-button {
  padding: 8px 16px;
  background: white;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  color: #475569;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
}

.simcontrol-dashboard .action-button:hover:not(:disabled) {
  background: #f8fafc;
}

.simcontrol-dashboard .action-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.simcontrol-dashboard .refresh-btn { color: #3B82F6; border-color: #DBEAFE; }
.simcontrol-dashboard .refresh-btn:hover:not(:disabled) { background: #EFF6FF; border-color: #3B82F6; }

.simcontrol-dashboard .table-container {
  overflow: auto;
  position: relative;
  max-height: 600px;
}

.simcontrol-dashboard .data-table {
  width: 100%;
  border-collapse: collapse;
}

.simcontrol-dashboard .data-table thead {
  position: sticky;
  top: 0;
  background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
  z-index: 10;
}

.simcontrol-dashboard .data-table th {
  padding: 12px 16px;
  text-align: left;
  color: white;
  font-weight: 600;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
  cursor: pointer;
}

.simcontrol-dashboard .search-row { background: #fff3e0 !important; }

.simcontrol-dashboard .search-row .search-cell {
  background: transparent !important;
  padding: 6px !important;
  cursor: default;
}

.simcontrol-dashboard .column-search-input {
  width: 100%;
  padding: 5px 8px;
  border: 1px solid #ffcc80;
  border-radius: 4px;
  font-size: 0.75rem;
  background: white;
  color: #333;
  font-weight: normal;
  text-transform: none;
  letter-spacing: normal;
}

.simcontrol-dashboard .column-search-input:focus {
  outline: none;
  border-color: #ff9800;
  box-shadow: 0 0 0 2px rgba(255, 152, 0, 0.2);
}

.simcontrol-dashboard .data-table tbody tr {
  border-bottom: 1px solid #e2e8f0;
  transition: all 0.2s;
  background: #fff;
  cursor: pointer;
}

.simcontrol-dashboard .data-table tbody tr:hover { background: #f1f5f9; }

.simcontrol-dashboard .data-table td {
  padding: 12px 16px;
  font-size: 0.875rem;
  color: #334155;
}

.simcontrol-dashboard .registration-badge {
  display: inline-block;
  background: linear-gradient(135deg, #3b82f6 0%, #6366f1 100%);
  color: white;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 600;
  font-family: monospace;
}

.simcontrol-dashboard .activity-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  color: white;
}

.simcontrol-dashboard .badge-MANAGED { background: linear-gradient(135deg, #27AE60, #229954); }
.simcontrol-dashboard .badge-SUSPENDED { background: linear-gradient(135deg, #E74C3C, #C0392B); }
.simcontrol-dashboard .badge-PAUSED { background: linear-gradient(135deg, #F39C12, #E67E22); }
.simcontrol-dashboard .badge-STAGED { background: linear-gradient(135deg, #3498DB, #2980B9); }

.simcontrol-dashboard .status-pill {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 700;
}

.simcontrol-dashboard .level-critical { background: rgba(231, 76, 60, 0.15); color: #E74C3C; border: 1px solid rgba(231, 76, 60, 0.3); }
.simcontrol-dashboard .level-low { background: rgba(255, 152, 0, 0.15); color: #FF9800; border: 1px solid rgba(255, 152, 0, 0.3); }
.simcontrol-dashboard .level-ok { background: rgba(39, 174, 96, 0.15); color: #27AE60; border: 1px solid rgba(39, 174, 96, 0.3); }

.simcontrol-dashboard .status-dot-new {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
}

.simcontrol-dashboard .status-dot-new.processed { background: #10b981; }
.simcontrol-dashboard .status-dot-new.idle { background: #94a3b8; }

.simcontrol-dashboard .loading-overlay {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(241, 245, 249, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
}

.simcontrol-dashboard .loader {
  width: 48px;
  height: 48px;
  border: 4px solid #FFE4B5;
  border-top: 4px solid #D2691E;
  border-radius: 50%;
  animation: sc-spin 1s linear infinite;
}

@keyframes sc-spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.simcontrol-dashboard .empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #334155;
  background: #f8fafc;
}

.simcontrol-dashboard .empty-icon { font-size: 4rem; color: #FFE4B5; margin-bottom: 16px; }
.simcontrol-dashboard .empty-title { font-size: 1.25rem; font-weight: 600; margin-bottom: 8px; }
.simcontrol-dashboard .empty-description { color: #6c757d; }

.simcontrol-dashboard .pagination-container {
  padding: 16px 20px;
  border-top: 1px solid #cbd5e1;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f1f5f9;
  flex-wrap: wrap;
  gap: 12px;
}

.simcontrol-dashboard .pagination-info { color: #6c757d; font-size: 0.875rem; }

.simcontrol-dashboard .pagination-controls { display: flex; gap: 6px; }

.simcontrol-dashboard .page-btn {
  padding: 6px 12px;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  color: #495057;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  font-weight: 500;
  min-width: 40px;
}

.simcontrol-dashboard .page-btn:hover:not(:disabled) { border-color: #D2691E; color: #D2691E; }
.simcontrol-dashboard .page-btn.active { background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%); color: white; border-color: #D2691E; }
.simcontrol-dashboard .page-btn:disabled { opacity: 0.5; cursor: not-allowed; }

.simcontrol-dashboard .simple-popup {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
}

.simcontrol-dashboard .popup-content {
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 500px;
  max-height: 85vh;
  overflow: auto;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.simcontrol-dashboard .popup-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
  color: white;
  border-radius: 8px 8px 0 0;
}

.simcontrol-dashboard .popup-header h3 { margin: 0; font-size: 1.25rem; }

.simcontrol-dashboard .close-btn {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  font-size: 20px;
  cursor: pointer;
  width: 32px;
  height: 32px;
  border-radius: 50%;
}

.simcontrol-dashboard .popup-body { padding: 20px; }

.simcontrol-dashboard .export-columns-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 6px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 10px;
  max-height: 200px;
  overflow-y: auto;
}

.simcontrol-dashboard .export-column-option {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8rem;
  cursor: pointer;
}

@media (max-width: 768px) {
  .simcontrol-dashboard .stats-container,
  .simcontrol-dashboard .search-controls,
  .simcontrol-dashboard .charts-section {
    grid-template-columns: 1fr;
  }
  .simcontrol-dashboard .pagination-container {
    flex-direction: column;
    align-items: stretch;
  }
}
</style>
